import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  getDescriptionText,
  notesWithDescription,
} from '@/components/investigations/InvestigationDossier'
import { useInvestigationsStoreApi } from '@/stores/investigations-store-provider'
import type { Investigation } from '@/types/investigation'

/** Edit state + save for an investigation's free-text description (stored as a
 *  special note). Re-syncs the draft when the persisted text changes remotely. */
export function useInvestigationDescriptionEditor(inv: Investigation) {
  const { t } = useTranslation()
  const storeApi = useInvestigationsStoreApi()
  const persisted = useMemo(() => getDescriptionText(inv), [inv])
  const [draft, setDraft] = useState(persisted)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setDraft(persisted)
  }, [persisted])

  const isDirty = draft.trim() !== persisted.trim()
  const disabled = inv.readOnly

  const save = async () => {
    if (!isDirty || disabled) return
    setSaving(true)
    try {
      await storeApi.getState().updateById(inv.id, { notes: notesWithDescription(inv, draft) })
      toast.success(t('investigations.detail.description.saved'))
    } catch (err) {
      toast.error(`${t('investigations.detail.description.saveFailed')}: ${(err as Error).message}`)
    } finally {
      setSaving(false)
    }
  }

  const reset = () => setDraft(persisted)

  return { draft, setDraft, saving, isDirty, disabled, save, reset }
}
